import { getLabelPrintBatchSearchValue } from "@bcc/contexts/batches-context.utils";
import { generateTestId } from "@client-shared";
import type { LabelPrintBatch, LabelPrintBatchStatus } from "@shared/types";

export const BATCH_STATUS_FILTER_VALUES = ["Processing", "Failed-for-Retry", "Completed", "Failed"] as const;

export type BatchStatusFilterValue = "all" | (typeof BATCH_STATUS_FILTER_VALUES)[number];

export const getBatchStatusFilterValue = (value?: string | null): BatchStatusFilterValue => {
  if (value && BATCH_STATUS_FILTER_VALUES.includes(value as (typeof BATCH_STATUS_FILTER_VALUES)[number])) {
    return value as BatchStatusFilterValue;
  }

  return "all";
};

// Maps raw API status values to the display labels shown in the UI.
// Only includes statuses whose display label differs from the raw value.
const BATCH_STATUS_DISPLAY_NAMES: Partial<Record<LabelPrintBatchStatus, string>> = {
  "Failed-for-Retry": "Retrying",
};

export const getVisibleBatches = ({
  batches,
  searchValue,
  statusFilter,
}: {
  batches: LabelPrintBatch[];
  searchValue: string;
  statusFilter: BatchStatusFilterValue;
}): LabelPrintBatch[] => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  return batches.filter((batch) => {
    const matchesStatus = statusFilter === "all" || batch.batchStatus === statusFilter;

    if (!matchesStatus) {
      return false;
    }

    if (!normalizedSearchValue) {
      return true;
    }

    const baseSearchValue = getLabelPrintBatchSearchValue(batch);
    const displayName = BATCH_STATUS_DISPLAY_NAMES[batch.batchStatus];
    const searchableValue = displayName ? `${baseSearchValue} ${displayName.toLowerCase()}` : baseSearchValue;

    return searchableValue.includes(normalizedSearchValue);
  });
};

export const setBatchesViewTestId = (identifier: string): string => generateTestId("BatchesView", identifier);

export const setBatchesViewFilterTestId = (status: BatchStatusFilterValue): string => setBatchesViewTestId(`filter-${status}`);

export const setBatchesViewSummaryTestId = (): string => setBatchesViewTestId("summary");

export const setBatchesViewActiveQueryTestId = (): string => setBatchesViewTestId("active-query");
